import React, { useEffect, useState } from 'react';
import { saveAs } from 'file-saver';
import styled from 'styled-components';
import { Button, Table } from '~/components';
import { useApi } from '~/contexts';
import { colors } from '~/styles';
import { DownloadArea, EmptyReport, Report, ReportLoader, Workspace } from './components';

const TopRow = styled(Table.Row)`
  align-items: flex-start;
`;

const CellContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.25rem 0;
`;

const Indent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-left: 3.25rem;
`;

const MemberName = styled.div`
  font-size: 0.875rem;
  font-weight: ${({ $bold }) => ($bold ? 'bold' : 'normal')};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const MemberEmail = styled.div`
  color: ${colors.grey55};
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const Flags = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.8125rem;
  color: ${colors.grey55};
`;

const Flag = styled.span`
  font-weight: ${({ $bold }) => ($bold ? 'bold' : 'normal')};
  color: ${({ $bold }) => ($bold ? colors.black : colors.grey55)};
`;

export default function MissingAssociatedMembers() {
  const api = useApi();
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [data, setData] = useState();

  useEffect(() => {
    const getReport = async () => {
      setIsLoading(true);
      setData();
      try {
        const { data } = await api.admin.reports.getMissingAssociatedMembers();
        setData(data);
      } finally {
        setIsLoading(false);
      }
    };
    getReport();
    return getReport.cancel;
  }, [api]);

  const handleDownload = async () => {
    setIsDownloading(true);

    try {
      const response = await api.admin.reportCSVs.getMissingAssociatedMembers();
      const disposition = response.headers['content-disposition'];
      const filenameMatch = disposition.match(/filename[^;=\n]*=((['"])(?<name>.*)?\2|[^;\n]*)/i);
      const filename = filenameMatch.groups['name'];
      const blob = new Blob([response.data], { type: 'text/csv' });

      saveAs(blob, filename);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return <ReportLoader />;
  }
  if (!data || !data.length) {
    return <EmptyReport />;
  }
  return (
    <Report>
      <Table>
        <Table.Header>
          <Table.Column>Workspace</Table.Column>
          <Table.Column>Associated Workspace</Table.Column>
        </Table.Header>
        {data.map((item) => {
          const isMismatched = item.associatedMemberName != null;
          const nameDiffers = isMismatched && item.memberName !== item.associatedMemberName;
          const billableDiffers = isMismatched && item.memberIsBillable !== item.associatedMemberIsBillable;
          const activeDiffers = isMismatched && item.memberIsActive !== item.associatedMemberIsActive;
          return (
            <TopRow key={`${item.workspaceId}_${item.memberEmail}`}>
              <Table.Cell>
                <CellContent>
                  <Workspace
                    data={{
                      name: item.workspaceName,
                      key: item.workspaceKey,
                      imageUrl: item.workspaceImageUrl,
                    }}
                  />
                  <Indent>
                    <div>
                      <MemberName $bold={nameDiffers}>{item.memberName}</MemberName>
                      <MemberEmail>{item.memberEmail}</MemberEmail>
                    </div>
                    <Flags>
                      <Flag $bold={billableDiffers}>Billable: {item.memberIsBillable ? 'Yes' : 'No'}</Flag>
                      <Flag $bold={activeDiffers}>Active: {item.memberIsActive ? 'Yes' : 'No'}</Flag>
                    </Flags>
                  </Indent>
                </CellContent>
              </Table.Cell>
              <Table.Cell>
                <CellContent>
                  <Workspace
                    data={{
                      name: item.associatedWorkspaceName,
                      key: item.associatedWorkspaceKey,
                      imageUrl: item.associatedWorkspaceImageUrl,
                    }}
                  />
                  <Indent>
                    {isMismatched ? (
                      <>
                        <div>
                          <MemberName $bold={nameDiffers}>{item.associatedMemberName}</MemberName>
                          <MemberEmail>{item.associatedMemberEmail}</MemberEmail>
                        </div>
                        <Flags>
                          <Flag $bold={billableDiffers}>
                            Billable: {item.associatedMemberIsBillable ? 'Yes' : 'No'}
                          </Flag>
                          <Flag $bold={activeDiffers}>Active: {item.associatedMemberIsActive ? 'Yes' : 'No'}</Flag>
                        </Flags>
                      </>
                    ) : (
                      <>
                        <div />
                        <div />
                      </>
                    )}
                  </Indent>
                </CellContent>
              </Table.Cell>
            </TopRow>
          );
        })}
      </Table>
      <DownloadArea>
        <Button isLoading={isDownloading} onClick={handleDownload}>
          Download CSV
        </Button>
      </DownloadArea>
    </Report>
  );
}
